import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled, { createGlobalStyle } from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalBase from 'LuxKit/Modal/ModalBase'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import Heading from 'LuxKit/Typography/Heading'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getImageKey } from '../offerGalleryImageUtils'
import GalleryV2CategoryStrip from './GalleryV2CategoryStrip'
import GalleryV2CategorySection from './GalleryV2CategorySection'
import GalleryV2PhotoGrid from './GalleryV2PhotoGrid'
import GalleryV2ImageViewer from './GalleryV2ImageViewer'
import { ALL_PHOTOS_CATEGORY_ID, GALLERY_CONTENT_MAX_WIDTH } from './GalleryModalV2.types'
import type { GalleryCategory } from './GalleryModalV2.types'

const FullScreenModalStyles = createGlobalStyle`
  ${mediaQueryUp.tablet} {
    div[data-gallery-v2].size-max {
      max-width: 100% !important;
      max-height: 100% !important;
      border-radius: 0 !important;
    }
  }
`

const GalleryLayout = styled.div`
  grid-row: 1 / -1;
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  min-width: 0;
`

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  height: ${rem(64)};
  padding-block-start: ${rem(12)};
  padding-block-end: ${rem(12)};
  padding-inline-start: ${rem(8)};
  padding-inline-end: ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const HeaderTitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const HeaderTitle = styled(Heading)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const GalleryScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`

const GridFrame = styled.div`
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  max-width: ${rem(GALLERY_CONTENT_MAX_WIDTH)};
  margin: 0 auto;
`

interface ViewerState {
  images: Array<App.Image>
  index: number
}

type GalleryViewMode = 'grid' | 'single'

function getOfferPropertyName(offer?: App.AnyOffer) {
  if (!offer) return ''
  if ('property' in offer && offer.property?.name) return offer.property.name
  if ('name' in offer && typeof offer.name === 'string') return offer.name
  return ''
}

interface Props {
  offer?: App.AnyOffer
  images: Array<App.Image>
  categories?: Array<GalleryCategory>
  scrollToPackageOptions?: () => void
}

function GalleryModalV2(props: Props) {
  const {
    offer,
    images,
    categories = EmptyArray as Array<GalleryCategory>,
    scrollToPackageOptions,
  } = props
  const intl = useIntl()
  const elementContext = useModalElementContext()

  const hasCategories = categories.length > 0

  const [activeCategoryId, setActiveCategoryId] = useState<string>(ALL_PHOTOS_CATEGORY_ID)

  const sectionCategories = useMemo(
    () => categories.filter(category => category.id !== ALL_PHOTOS_CATEGORY_ID),
    [categories],
  )

  const activeCategory = useMemo(
    () => categories.find(category => category.id === activeCategoryId),
    [categories, activeCategoryId],
  )

  const allPhotosCategory = useMemo(
    () => categories.find(category => category.id === ALL_PHOTOS_CATEGORY_ID),
    [categories],
  )

  const isAllPhotosActive = activeCategoryId === ALL_PHOTOS_CATEGORY_ID

  const handleSelectCategory = useCallback((categoryId: string) => {
    setActiveCategoryId(categoryId)
  }, [])

  const handleClose = useCallback(() => {
    elementContext?.resolve()
  }, [elementContext])

  const [scrollTargetRoomId, setScrollTargetRoomId] = useState<string | undefined>(undefined)
  const [shouldScrollToPackageOptions, setShouldScrollToPackageOptions] = useState(false)

  const [viewMode, setViewMode] = useState<GalleryViewMode>('grid')
  const [viewerState, setViewerState] = useState<ViewerState | undefined>(undefined)
  const [scrollTargetImageKey, setScrollTargetImageKey] = useState<string | undefined>(undefined)

  const handleImageClick = useCallback((clickedImages: Array<App.Image>, index: number) => {
    if (isAllPhotosActive && allPhotosCategory) {
      const clickedImage = clickedImages[index]
      const clickedImageKey = getImageKey(clickedImage, index)
      const globalIndex = allPhotosCategory.images.findIndex(
        (image, imageIndex) => getImageKey(image, imageIndex) === clickedImageKey,
      )
      setViewerState({ images: allPhotosCategory.images, index: Math.max(globalIndex, 0) })
      setViewMode('single')
      return
    }

    setViewerState({ images: clickedImages, index })
    setViewMode('single')
  }, [isAllPhotosActive, allPhotosCategory])

  const handleViewerImageChange = useCallback((index: number) => {
    setViewerState(prev => (prev ? { ...prev, index } : prev))
  }, [])

  const handleCloseViewer = useCallback(() => {
    if (viewerState) {
      setScrollTargetImageKey(getImageKey(viewerState.images[viewerState.index], viewerState.index))
    }
    setViewMode('grid')
  }, [viewerState])

  useEffect(() => {
    if (viewMode !== 'grid' || !scrollTargetImageKey) return

    requestAnimationFrame(() => {
      const tiles = document.querySelectorAll<HTMLElement>('[data-image-key]')
      const target = Array.from(tiles).find(tile => tile.dataset.imageKey === scrollTargetImageKey)
      target?.scrollIntoView({ behavior: 'auto', block: 'center' })
    })
    setScrollTargetImageKey(undefined)
  }, [viewMode, scrollTargetImageKey])

  // Esc/backdrop-close on the single modal should back out of the photo viewer first,
  // and only close the whole gallery when we're already on the grid.
  const handleModalClose = useCallback(() => {
    if (viewMode === 'single') {
      handleCloseViewer()
      return
    }
    handleClose()
  }, [viewMode, handleCloseViewer, handleClose])

  const scrollToRoom = useCallback((roomId: string) => {
    const anchors = Array.from(document.querySelectorAll<HTMLElement>(`#${CSS.escape(roomId)}`))
    const packageOptionsAnchor = document.getElementById('Package-Options')
    const visibleAnchors = anchors.filter(anchor => anchor.getClientRects().length > 0)
    const anchor = (
      visibleAnchors.find(a => packageOptionsAnchor?.contains(a))
      ?? visibleAnchors[0]
      ?? anchors.find(a => packageOptionsAnchor?.contains(a))
      ?? anchors[0]
      ?? null
    )
    if (!anchor) {
      scrollToPackageOptions?.()
      return
    }
    requestAnimationFrame(() => {
      anchor.scrollIntoView({ behavior: 'auto' })
    })
  }, [scrollToPackageOptions])

  const handleViewPackagesClick = useCallback(() => {
    setShouldScrollToPackageOptions(true)
    handleClose()
  }, [handleClose])

  const handleViewRoom = useCallback((categoryId: string) => {
    setScrollTargetRoomId(categoryId)
    handleClose()
  }, [handleClose])

  const handleModalAfterClose = useCallback(() => {
    if (scrollTargetRoomId) {
      scrollToRoom(scrollTargetRoomId)
      setScrollTargetRoomId(undefined)
    } else if (shouldScrollToPackageOptions) {
      scrollToPackageOptions?.()
      setShouldScrollToPackageOptions(false)
    }
  }, [scrollTargetRoomId, scrollToRoom, shouldScrollToPackageOptions, scrollToPackageOptions])

  return <>
    <FullScreenModalStyles />
    <ModalBase size="max" height="full" onClose={handleModalClose} onAfterClose={handleModalAfterClose} data-gallery-v2>
      <GalleryLayout>
        {viewMode === 'grid' && <>
          <HeaderBar>
            <HeaderTitleArea>
              <IconButton
                kind="tertiary"
                variant="default"
                onClick={handleClose}
                aria-label={intl.formatMessage({
                  id: 'Bj6WTY_NRyBBKtSUbHuW',
                  defaultMessage: 'Close gallery',
                  description: 'Aria label for the header back arrow button that closes the full screen offer page gallery modal',
                })}
              >
                <LineArrowLeftIcon />
              </IconButton>
              <HeaderTitle variant="heading6" colour="normal-contrast" lineClamp={1}>
                {getOfferPropertyName(offer)}
              </HeaderTitle>
            </HeaderTitleArea>
            <TextButton kind="primary" variant="default" onClick={handleViewPackagesClick}>
              {intl.formatMessage({
                id: 'xJTycxTbTs2_WqgXhY30',
                defaultMessage: 'View packages',
                description: 'Button label shown in the full screen offer page gallery header for navigating to the available packages',
              })}
            </TextButton>
          </HeaderBar>
          <GalleryScrollArea>
            {hasCategories && <GalleryV2CategoryStrip
              categories={categories}
              activeCategoryId={activeCategoryId}
              onSelectCategory={handleSelectCategory}
            />}
            {hasCategories && isAllPhotosActive && sectionCategories.map(category => (
              <GalleryV2CategorySection
                key={category.id}
                category={category}
                onImageClick={handleImageClick}
                onViewRoom={handleViewRoom}
              />
            ))}
            {hasCategories && !isAllPhotosActive && activeCategory && <GridFrame>
              <GalleryV2PhotoGrid
                images={activeCategory.images}
                onImageClick={handleImageClick}
              />
            </GridFrame>}
            {!hasCategories && <GridFrame>
              <GalleryV2PhotoGrid
                images={images}
                onImageClick={handleImageClick}
              />
            </GridFrame>}
          </GalleryScrollArea>
        </>}
        {viewMode === 'single' && viewerState && <GalleryV2ImageViewer
          images={viewerState.images}
          defaultIndex={viewerState.index}
          onClose={handleCloseViewer}
          onImageChange={handleViewerImageChange}
        />}
      </GalleryLayout>
    </ModalBase>
  </>
}

export default GalleryModalV2
